import * as React from 'react';

import { AsyncBoundary, cn, Col, Widget } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { tradeRoutePaths as tradeRoutes } from '../../../routes';
import { formatAddressDisplay } from '../../purchase-orders/helpers/formatAddress';
import { useCreateInboundTicket } from '../hooks/useCreateInboundTicket';
import { useCreateOutboundTicket } from '../hooks/useCreateOutboundTicket';
import { useListKeyboardShortcuts } from '../hooks/useListKeyboardShortcuts';
import { useTicketListFilter } from '../hooks/useTicketListFilter';

import { TicketListFilters } from './TicketListFilters';
import { TicketSidebarCard } from './TicketSidebarCard';

import { $sidebarCollapsed } from '~/app/state/sidebar.state';
import { $flushTicketDetailsAutoSave } from '~/modules/trade/_state/scaleTicketInboundInfoForm.state';
import { $saveInboundMaterials } from '~/modules/trade/_state/scaleTicketInboundMaterialsForm.state';
import { $flushOutboundCustomerAutoSave } from '~/modules/trade/_state/scaleTicketOutboundCustomerForm.state';
import { $flushOutboundDetailsAutoSave } from '~/modules/trade/_state/scaleTicketOutboundInfoForm.state';
import { $saveOutboundMaterials } from '~/modules/trade/_state/scaleTicketOutboundMaterialsForm.state';
import { $flushOutboundNotesAutoSave } from '~/modules/trade/_state/scaleTicketOutboundNotesForm.state';
import {
  $inboundMineOnly,
  $scaleTicketId,
  $ticketDirection,
} from '~/modules/trade/_state/scaleTicketPro.state';
import { $flushTicketSupplierAutoSave } from '~/modules/trade/_state/scaleTicketSupplierForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';
import { PurchaseLoadRecord } from '~/modules/trade/types/purchaseLoad';
import { useIdentity } from '~/shared/hooks/useIdentity';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const getNotesPreview = (
  documentNumber?: string,
  internalNotes?: string,
  prefix?: string,
): string | null => {
  if (documentNumber) {
    const notes = internalNotes ? ` · ${internalNotes}` : '';
    return `${prefix}${documentNumber}${notes}`;
  }
  return internalNotes ?? null;
};

const getSupplierAddress = (ticket: PurchaseLoadRecord): string | null => {
  if (!ticket.supplierAddress) return null;
  const formatted = formatAddressDisplay(ticket.supplierAddress.address ?? {});
  return formatted !== 'Address'
    ? formatted
    : (ticket.supplierAddress.name ?? null);
};

//
// ⚛️ ⚛️ ⚛️ SIDEBAR EMPTY STATE ⚛️ ⚛️ ⚛️
//
const SidebarEmptyState = () => {
  const t = useTranslations();

  return (
    <div className="@SidebarEmptyState flex h-32 items-center justify-center">
      <div className="text-foreground/50">{t.scaleTicketPro.noTickets}</div>
    </div>
  );
};

//
// ⚛️ ⚛️ ⚛️ INBOUND SIDEBAR CONTENT ⚛️ ⚛️ ⚛️
//
type InboundSidebarContentProps = { compact: boolean };

const InboundSidebarContent = ({ compact }: InboundSidebarContentProps) => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useModuleLocation();

  // Atoms
  const selectedTicketId = useAtomValue($scaleTicketId);
  const [mineOnly, setMineOnly] = useAtom($inboundMineOnly);
  const flushDetailsAutoSave = useSetAtom($flushTicketDetailsAutoSave);
  const flushSupplierAutoSave = useSetAtom($flushTicketSupplierAutoSave);
  const saveInboundMaterials = useSetAtom($saveInboundMaterials);

  // Data hooks
  const { user } = useIdentity();
  const currentUserId = user?.id;

  const {
    items,
    filter,
    handleSearchChange,
    handleStatusChange,
    handleStatusToggle,
    handleAccountToggle,
    handleClearAccounts,
    loadingState,
    refetch,
  } = useTicketListFilter({ direction: 'inbound', mineOnly, currentUserId });

  const filteredTickets = items as PurchaseLoadRecord[];

  const { createTicket, isCreating } = useCreateInboundTicket();

  // Callbacks
  const handleSelectTicket = React.useCallback(
    (id: string) => {
      if (selectedTicketId) {
        flushDetailsAutoSave(selectedTicketId);
        flushSupplierAutoSave(selectedTicketId);
        saveInboundMaterials({
          ticketId: selectedTicketId,
          immediate: true,
          failedToSaveMessage:
            t.scaleTicketPro.materialsTab.failedToSaveMaterials,
        });
      }
      navigate(tradeRoutes.scaleTicketDetail.replace(':id', id));
    },
    [
      navigate,
      selectedTicketId,
      flushDetailsAutoSave,
      flushSupplierAutoSave,
      saveInboundMaterials,
      t,
    ],
  );

  const handleCreateNew = React.useCallback(async () => {
    await createTicket();
  }, [createTicket]);

  useListKeyboardShortcuts({
    searchContainerSelector: '.\\@PurchaseOrderSidebar',
    onCreateNew: handleCreateNew,
    createModifier: 'alt',
  });

  return (
    <>
      <TicketListFilters
        layout="sidebar"
        direction="inbound"
        filter={filter}
        mineOnly={mineOnly}
        onMineOnlyChange={setMineOnly}
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        onStatusToggle={handleStatusToggle}
        onAccountToggle={handleAccountToggle}
        onClearAccounts={handleClearAccounts}
        onCreateNew={handleCreateNew}
        isCreating={isCreating}
        onRefresh={refetch}
      />

      <Col className="no-scrollbar flex-1 content-start overflow-y-auto px-1 pt-1">
        <AsyncBoundary loading={loadingState?.isInitialLoading} error={null}>
          {filteredTickets.length === 0 ? (
            <SidebarEmptyState />
          ) : (
            filteredTickets.map((ticket) => (
              <TicketSidebarCard
                key={ticket.id}
                id={ticket.id}
                documentNumber={ticket.documentNumber}
                contactType={t.scaleTicketPro.supplier}
                contactName={ticket.supplierAccount?.name}
                subtitle={getSupplierAddress(ticket)}
                notesPreview={getNotesPreview(
                  ticket.purchaseOrder?.documentNumber?.toLocaleString(),
                  ticket.internalNotes || '',
                  t.scaleTicketPro.purchaseOrderPrefix,
                )}
                lineItemCount={ticket.lineItemCount ?? 0}
                date={ticket.receivedDate}
                isSelected={ticket.id === selectedTicketId}
                onSelect={() => handleSelectTicket(ticket.id)}
                compact={compact}
              />
            ))
          )}
        </AsyncBoundary>
      </Col>
    </>
  );
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND SIDEBAR CONTENT ⚛️ ⚛️ ⚛️
//
type OutboundSidebarContentProps = { compact: boolean };

const OutboundSidebarContent = ({ compact }: OutboundSidebarContentProps) => {
  const t = useTranslations();

  const [, navigate] = useModuleLocation();

  const selectedTicketId = useAtomValue($scaleTicketId);
  const [mineOnly, setMineOnly] = useAtom($inboundMineOnly);
  const flushDetailsAutoSave = useSetAtom($flushOutboundDetailsAutoSave);
  const flushCustomerAutoSave = useSetAtom($flushOutboundCustomerAutoSave);
  const flushNotesAutoSave = useSetAtom($flushOutboundNotesAutoSave);
  const saveOutboundMaterials = useSetAtom($saveOutboundMaterials);

  const { user } = useIdentity();
  const currentUserId = user?.id;

  const {
    items,
    filter,
    handleSearchChange,
    handleStatusChange,
    handleStatusToggle,
    handleAccountToggle,
    handleClearAccounts,
    loadingState,
    refetch,
  } = useTicketListFilter({ direction: 'outbound', mineOnly, currentUserId });

  const filteredLoads = items as LoadRecord[];

  const { createTicket, isCreating } = useCreateOutboundTicket();

  const handleSelectTicket = React.useCallback(
    (id: string) => {
      if (selectedTicketId) {
        flushDetailsAutoSave(selectedTicketId);
        flushCustomerAutoSave(selectedTicketId);
        flushNotesAutoSave(selectedTicketId);
        saveOutboundMaterials({
          ticketId: selectedTicketId,
          immediate: true,
          failedToSaveMessage:
            t.scaleTicketPro.materialsTab.failedToSaveMaterials,
        });
      }
      navigate(tradeRoutes.scaleTicketDetail.replace(':id', id));
    },
    [
      navigate,
      selectedTicketId,
      flushDetailsAutoSave,
      flushCustomerAutoSave,
      flushNotesAutoSave,
      saveOutboundMaterials,
      t,
    ],
  );

  const handleCreateNew = React.useCallback(async () => {
    await createTicket();
  }, [createTicket]);

  useListKeyboardShortcuts({
    searchContainerSelector: '.\\@PurchaseOrderSidebar',
    onCreateNew: handleCreateNew,
    createModifier: 'alt',
  });

  return (
    <>
      <TicketListFilters
        layout="sidebar"
        direction="outbound"
        filter={filter}
        mineOnly={mineOnly}
        onMineOnlyChange={setMineOnly}
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        onStatusToggle={handleStatusToggle}
        onAccountToggle={handleAccountToggle}
        onClearAccounts={handleClearAccounts}
        onCreateNew={handleCreateNew}
        isCreating={isCreating}
        onRefresh={refetch}
      />

      <div className="no-scrollbar flex-1 overflow-y-auto px-1 pt-1">
        <AsyncBoundary loading={loadingState?.isInitialLoading} error={null}>
          {filteredLoads.length === 0 ? (
            <SidebarEmptyState />
          ) : (
            filteredLoads.map((load) => (
              <TicketSidebarCard
                key={load.id}
                id={load.id}
                documentNumber={load.loadNumber}
                contactType={t.scaleTicketPro.customer}
                contactName={load.customerName}
                subtitle={load.salesOrderNumber || ''}
                notesPreview={load.internalNotes || ''}
                lineItemCount={
                  load.lineItemCount ?? load.lineItems?.length ?? 0
                }
                date={load.timestamps.createdAt}
                isSelected={load.id === selectedTicketId}
                onSelect={() => handleSelectTicket(load.id)}
                compact={compact}
              />
            ))
          )}
        </AsyncBoundary>
      </div>
    </>
  );
};

//
// ⚛️ ⚛️ ⚛️ TICKET SIDEBAR ⚛️ ⚛️ ⚛️
//
export const TicketSidebar = () => {
  const direction = useAtomValue($ticketDirection);
  const sidebarCollapsed = useAtomValue($sidebarCollapsed);

  return (
    <div
      className={cn(
        '@TicketSidebar flex h-full flex-col',
        sidebarCollapsed ? 'max-w-70 min-w-70' : 'max-w-56 min-w-56',
      )}
    >
      <Widget className="flex h-full flex-col">
        {direction === 'inbound' ? (
          <InboundSidebarContent compact={!sidebarCollapsed} />
        ) : (
          <OutboundSidebarContent compact={!sidebarCollapsed} />
        )}
      </Widget>
    </div>
  );
};
